import { BusinessConfig } from '../../../types';
import { resolveBookingUxConfig } from '../../../utils/bookingUxConfig';

export type SitePresentationSettings = {
  showEmployeeBio: boolean;
  showEmployeeSocials: boolean;
  showEmployeeProfilePhoto: boolean;
  resourceSingular: string;
  resourcePlural: string;
  anyAvailableLabel: string;
  selectResourceLabel: string;
};

export const sitePresentationSettingsFromConfig = (
  config?: BusinessConfig | null
): SitePresentationSettings => {
  const resolved = resolveBookingUxConfig(config);
  return {
    showEmployeeBio: resolved.uiFlags.showEmployeeBio,
    showEmployeeSocials: resolved.uiFlags.showEmployeeSocials,
    showEmployeeProfilePhoto: resolved.uiFlags.showEmployeeProfilePhoto,
    resourceSingular: resolved.terminology.resourceSingular,
    resourcePlural: resolved.terminology.resourcePlural,
    anyAvailableLabel: resolved.terminology.anyAvailableLabel,
    selectResourceLabel: resolved.terminology.selectResourceLabel,
  };
};

export const normalizeSitePresentationSettings = (
  settings: SitePresentationSettings
): SitePresentationSettings => {
  const normalized = {
    ...settings,
    resourceSingular: settings.resourceSingular.trim(),
    resourcePlural: settings.resourcePlural.trim(),
    anyAvailableLabel: settings.anyAvailableLabel.trim(),
    selectResourceLabel: settings.selectResourceLabel.trim(),
  };
  const labels = [
    normalized.resourceSingular,
    normalized.resourcePlural,
    normalized.anyAvailableLabel,
    normalized.selectResourceLabel,
  ];
  if (labels.some((label) => !label)) throw new Error('Booking labels cannot be blank.');
  return normalized;
};

export const sitePresentationSettingsEqual = (
  left: SitePresentationSettings,
  right: SitePresentationSettings
): boolean => JSON.stringify(left) === JSON.stringify(right);

export const presentationPreferencesPatch = (
  settings: SitePresentationSettings,
  current?: BusinessConfig['preferences']
): Partial<BusinessConfig['preferences']> => ({
  uiFlags: {
    ...(current?.uiFlags ?? {}),
    showEmployeeBio: settings.showEmployeeBio,
    showEmployeeSocials: settings.showEmployeeSocials,
    showEmployeeProfilePhoto: settings.showEmployeeProfilePhoto,
  },
  terminology: {
    ...(current?.terminology ?? {}),
    resourceSingular: settings.resourceSingular,
    resourcePlural: settings.resourcePlural,
    anyAvailableLabel: settings.anyAvailableLabel,
    selectResourceLabel: settings.selectResourceLabel,
  },
});

export const applySitePresentationSettings = (
  config: BusinessConfig,
  settings: SitePresentationSettings
): BusinessConfig => ({
  ...config,
  preferences: {
    ...(config.preferences ?? {}),
    ...presentationPreferencesPatch(settings, config.preferences),
  },
});
